import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { safeStorage } from './safeStorage'

export type Direction = 'current' | 'command' | 'saas' | 'expressive'

export const DIRECTIONS: { id: Direction; labelKey: string }[] = [
  { id: 'current', labelKey: 'direction.current' },
  { id: 'command', labelKey: 'direction.command' },
  { id: 'saas', labelKey: 'direction.saas' },
  { id: 'expressive', labelKey: 'direction.expressive' },
]

export function migrateDirection(persisted: unknown, _version: number): { direction: Direction } {
  const d = (persisted as { direction?: string } | undefined)?.direction
  const valid: Direction[] = ['current', 'command', 'saas', 'expressive']
  return { direction: d === 'refined' ? 'command' : (valid.includes(d as Direction) ? (d as Direction) : 'current') }
}

// Brief global cross-fade so theme switches feel intentional, not jarring.
// The class is defined in index.css and removed after the transition window.
let transitionTimer: number | undefined

function applyDirection(direction: Direction) {
  const root = document.documentElement
  root.classList.add('theme-switching')
  if (direction === 'current') {
    delete root.dataset.theme   // current = untouched :root/.dark tokens
  } else {
    root.dataset.theme = direction
  }
  window.clearTimeout(transitionTimer)
  transitionTimer = window.setTimeout(() => root.classList.remove('theme-switching'), 350)
}

interface DirectionState {
  direction: Direction
  setDirection: (direction: Direction) => void
  cycleDirection: () => void
}

export const useDirectionStore = create<DirectionState>()(
  persist(
    (set, get) => ({
      direction: 'current',
      setDirection: (direction) => {
        applyDirection(direction)
        set({ direction })
      },
      cycleDirection: () => {
        const ids = DIRECTIONS.map(d => d.id)
        const next = ids[(ids.indexOf(get().direction) + 1) % ids.length]
        get().setDirection(next)
      },
    }),
    {
      name: 'direction-storage',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({ direction: state.direction }),
      version: 1,
      migrate: migrateDirection,
      onRehydrateStorage: () => (state) => {
        if (state) applyDirection(state.direction)
      },
    }
  )
)
